// components/ui/Tabs.jsx
import React, { useState, createContext, useContext } from 'react';
import { cn } from '../../utils/helpers';

// Tabs Context
const TabsContext = createContext();

const Tabs = ({ 
  defaultValue, 
  value, 
  onValueChange, 
  orientation = "horizontal",
  className,
  children,
  ...props 
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const handleValueChange = (newValue) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  return (
    <TabsContext.Provider
      value={{
        value: currentValue,
        onValueChange: handleValueChange,
        orientation
      }}
    >
      <div
        className={cn(
          "w-full",
          orientation === "vertical" && "flex",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
};

const TabsList = React.forwardRef(({ className, ...props }, ref) => {
  const { orientation } = useContext(TabsContext);
  
  return (
    <div
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center rounded-2xl border border-slate-200/80 bg-slate-100/90 p-1 text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400",
        orientation === "vertical" 
          ? "flex-col h-fit space-y-1 p-1" 
          : "min-h-[44px] space-x-1 overflow-x-auto",
        className
      )}
      role="tablist"
      aria-orientation={orientation}
      {...props}
    />
  );
});

TabsList.displayName = "TabsList";

const TabsTrigger = React.forwardRef(({ 
  className, 
  value, 
  disabled = false,
  children,
  ...props 
}, ref) => {
  const { value: selectedValue, onValueChange, orientation } = useContext(TabsContext);
  const isSelected = selectedValue === value;

  return (
    <button
      ref={ref}
      className={cn(
        "inline-flex min-h-[38px] items-center justify-center whitespace-nowrap rounded-xl px-3.5 py-2 text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        orientation === "vertical" ? "w-full justify-start" : "",
        isSelected
          ? "border border-slate-200 bg-white text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          : "hover:bg-white/60 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      disabled={disabled}
      onClick={() => !disabled && onValueChange(value)}
      type="button"
      role="tab"
      aria-selected={isSelected}
      aria-controls={`tabpanel-${value}`}
      id={`tab-${value}`}
      {...props}
    >
      {children}
    </button>
  );
});

TabsTrigger.displayName = "TabsTrigger";

const TabsContent = React.forwardRef(({ 
  className, 
  value,
  forceMount = false,
  children,
  ...props 
}, ref) => {
  const { value: selectedValue } = useContext(TabsContext);
  const isSelected = selectedValue === value;

  if (!isSelected && !forceMount) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={cn(
        "mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
        !isSelected && forceMount && "hidden",
        className
      )}
      role="tabpanel"
      aria-labelledby={`tab-${value}`}
      id={`tabpanel-${value}`}
      tabIndex={0}
      {...props}
    >
      {children}
    </div>
  );
});

TabsContent.displayName = "TabsContent";

// Enhanced Tabs with additional features
export const EnhancedTabs = ({ 
  items, 
  defaultValue,
  value,
  onValueChange,
  orientation = "horizontal",
  variant = "default",
  size = "default",
  showBadges = false,
  className,
  ...props 
}) => {
  const variants = {
    default: "bg-slate-100 dark:bg-slate-800",
    pills: "bg-transparent p-0 space-x-2",
    underline: "bg-transparent border-b border-slate-200 dark:border-slate-700 p-0 rounded-none",
    cards: "bg-transparent p-0 space-x-1"
  };

  const sizes = {
    sm: "h-8 text-xs",
    default: "h-10 text-sm",
    lg: "h-12 text-base"
  };

  const getTriggerVariantStyles = (variant) => {
    switch (variant) {
      case "pills":
        return "rounded-full px-4 py-2 bg-slate-100 dark:bg-slate-800 data-[state=active]:bg-blue-500 data-[state=active]:text-white";
      case "underline":
        return "rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 bg-transparent shadow-none pb-3";
      case "cards":
        return "rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 data-[state=active]:border-blue-500 data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-blue-950";
      default:
        return "";
    }
  };

  return (
    <Tabs
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      orientation={orientation}
      className={className}
      {...props}
    >
      <TabsList className={cn(variants[variant], sizes[size])}>
        {items.map((item) => (
          <TabsTrigger
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={cn(getTriggerVariantStyles(variant))}
          >
            <div className="flex items-center space-x-2">
              {item.icon && (
                <span className="w-4 h-4 flex items-center justify-center">
                  {item.icon}
                </span>
              )}
              <span>{item.label}</span>
              {showBadges && item.badge && (
                <span className="ml-2 px-2 py-0.5 bg-blue-500 text-white rounded-full text-xs font-medium min-w-[20px] h-5 flex items-center justify-center">
                  {item.badge}
                </span>
              )}
            </div>
          </TabsTrigger>
        ))}
      </TabsList>
      
      {items.map((item) => (
        <TabsContent key={item.value} value={item.value}>
          {item.content}
        </TabsContent>
      ))}
    </Tabs>
  );
};

// Trading specific tabs component
export const TradingTabs = ({ 
  activeTab, 
  onValueChange, // Changed from onTabChange to onValueChange
  tabs = ['spot', 'margin', 'futures'],
  className,
  showBalances = false,
  balances = {}
}) => {
  const tabLabels = {
    spot: 'Spot Trading',
    margin: 'Margin Trading',
    futures: 'Futures Trading',
    options: 'Options Trading',
    p2p: 'P2P Trading'
  };

  const tabIcons = {
    spot: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    margin: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    futures: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
      </svg>
    ),
    options: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    p2p: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  };

  const items = tabs.map(tab => ({
    value: tab,
    label: tabLabels[tab] || tab.charAt(0).toUpperCase() + tab.slice(1),
    icon: tabIcons[tab],
    badge: showBalances && balances[tab] ? balances[tab] : null,
    content: null // Content will be handled externally
  }));

  return (
    <div className={cn("w-full", className)}>
      <Tabs 
        value={activeTab} 
        onValueChange={onValueChange}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-5">
          {items.map((item) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              className="flex items-center space-x-2 px-3 py-2"
            >
              {item.icon}
              <span className="hidden sm:inline">{item.label}</span>
              <span className="sm:hidden">{item.value.toUpperCase()}</span>
              {item.badge && (
                <span className="ml-1 px-1.5 py-0.5 bg-blue-500 text-white rounded text-xs">
                  {item.badge}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
};

// Market Tabs for different market sections
export const MarketTabs = ({ 
  activeTab, 
  onValueChange, // Changed from onTabChange to onValueChange
  className 
}) => {
  const marketTabs = [
    {
      value: 'favorites',
      label: 'Favorites',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      )
    },
    {
      value: 'spot',
      label: 'Spot',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth={2} />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" />
        </svg>
      )
    },
    {
      value: 'margin',
      label: 'Margin',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      )
    },
    {
      value: 'futures',
      label: 'Futures',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    }
  ];

  return (
    <EnhancedTabs
      items={marketTabs}
      value={activeTab}
      onValueChange={onValueChange}
      variant="underline"
      className={className}
    />
  );
};

// Order Tabs for buy/sell interface
export const OrderTabs = ({ 
  activeTab, 
  onValueChange, // Changed from onTabChange to onValueChange
  className 
}) => {
  const orderTabs = [
    {
      value: 'limit',
      label: 'Limit',
      content: null
    },
    {
      value: 'market',
      label: 'Market',
      content: null
    },
    {
      value: 'stop',
      label: 'Stop-Limit',
      content: null
    }
  ];

  return (
    <EnhancedTabs
      items={orderTabs}
      value={activeTab}
      onValueChange={onValueChange}
      variant="pills"
      size="sm"
      className={className}
    />
  );
};

// Export all components
export { Tabs, TabsList, TabsTrigger, TabsContent };
export default Tabs;
